import { useState, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "../../../App";

/**
 * Address autocomplete hook for the Job form's street-address input.
 *
 * Scope: ONLY the typeahead behavior on the street input — debounced
 * Google Places suggestion fetch + place-details lookup + auto-fill of
 * city/state/zip when a suggestion is picked. The Address Picker modal
 * (saved-address cards + "+ Add New Address" form) is now handled by the
 * standalone `<CustomerAddressPicker>` component, so this hook is no
 * longer responsible for picker state.
 */
export function useAddressAutocomplete(setFormData) {
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressTimeoutRef = useRef(null);

  const handleAddressChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, customer_address: value }));
    if (addressTimeoutRef.current) clearTimeout(addressTimeoutRef.current);
    if (value.length < 3) { setAddressSuggestions([]); setShowAddressSuggestions(false); return; }
    addressTimeoutRef.current = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/places/autocomplete?input=${encodeURIComponent(value)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAddressSuggestions(response.data.predictions || []);
        setShowAddressSuggestions(true);
      } catch {
        setAddressSuggestions([{ description: value, place_id: "manual" }]);
      } finally { setAddressLoading(false); }
    }, 300);
  }, [setFormData]);

  const selectAddress = async (suggestion) => {
    const addr = suggestion.description || "";
    setFormData(prev => ({ ...prev, customer_address: addr }));
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
    if (suggestion.place_id && suggestion.place_id !== "manual") {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/places/details/${suggestion.place_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const details = res.data;
        setFormData(prev => ({
          ...prev,
          customer_address: details.street || details.formatted_address || addr,
          address_city: details.city || prev.address_city,
          address_state: details.state || prev.address_state,
          zip_code: details.zip || prev.zip_code,
          latitude: details.latitude ?? prev.latitude ?? null,
          longitude: details.longitude ?? prev.longitude ?? null,
        }));
      } catch {
        const zipMatch = addr.match(/\b(\d{5}(?:-\d{4})?)\b/);
        if (zipMatch) setFormData(prev => ({ ...prev, zip_code: zipMatch[1] }));
      }
    } else {
      const zipMatch = addr.match(/\b(\d{5}(?:-\d{4})?)\b/);
      if (zipMatch) setFormData(prev => ({ ...prev, zip_code: zipMatch[1] }));
    }
  };

  const resetAddress = useCallback(() => { setAddressSuggestions([]); setShowAddressSuggestions(false); }, []);

  return {
    addressSuggestions, showAddressSuggestions, addressLoading,
    handleAddressChange, selectAddress, setShowAddressSuggestions,
    resetAddress,
  };
}
